import type { ProfessionOPFilter, SpecialiteAnnuaireOP } from "../apis/monsisra/generated.v2";
import type { PreferencesRestriction } from "../apis/monsisra/generated";

export interface InputFilter {
	id: number;
	label: string;
	priority: number;
	optionKey: string;
	filterType: "profession" | "specialite";
	parentProfessionId?: number;
	parentProfessionLabel?: string;
}

export function normalizeSearchText(value: string): string {
	// Uniformise texte saisi et libellés avant la recherche (accents, ponctuation et casse ignorés).
	return value
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/gi, "")
		.toLowerCase()
		.trim();
}

function isValidNamedFilter(value: { id?: number; nom?: string }): boolean {
	return value.id !== undefined && Boolean(value.nom?.trim());
}

export function buildProfessionOptions(professions: ProfessionOPFilter[] = []): InputFilter[] {
	return (
		professions
			// Ignore les entrées incomplètes : une option doit toujours avoir un identifiant et un libellé.
			.filter(isValidNamedFilter)
			// Trie les professions par ordre alphabétique français, sans distinguer les accents ni la casse.
			.sort((left, right) => (left.nom ?? "").localeCompare(right.nom ?? "", "fr", { sensitivity: "base" }))
			// Produit une liste plate : chaque profession est immédiatement suivie de ses spécialités.
			.flatMap((profession) => {
				const professionLabel = profession.nom!.trim();
				const professionOption: InputFilter = {
					id: profession.id,
					label: professionLabel,
					priority: 0,
					optionKey: `profession:${profession.id}`,
					filterType: "profession",
				};
				const specialiteOptions = (profession.specialites ?? [])
					// Applique les mêmes règles de validité et de tri aux spécialités.
					.filter(isValidNamedFilter)
					.sort((left, right) => (left.nom ?? "").localeCompare(right.nom ?? "", "fr", { sensitivity: "base" }))
					.map(
						(specialite: SpecialiteAnnuaireOP): InputFilter => ({
							id: specialite.id!,
							// Le libellé conserve le contexte de la profession, notamment pour la recherche.
							label: `${professionLabel} - ${specialite.nom!.trim()}`,
							priority: 0,
							optionKey: `profession:${profession.id}:specialite:${specialite.id}`,
							filterType: "specialite",
							parentProfessionId: profession.id,
							parentProfessionLabel: professionLabel,
						})
					);
				return [professionOption, ...specialiteOptions];
			})
	);
}

export type ProfessionSpecialiteRestriction = Pick<PreferencesRestriction, "limitToAutorizedProfessionsAndSpecialites" | "autorizedProfessionIds" | "autorizedSpecialites">;

export function hasAuthorizedProfessionOrSpecialite(restriction?: ProfessionSpecialiteRestriction): boolean {
	return (restriction?.autorizedProfessionIds?.length ?? 0) > 0 || (restriction?.autorizedSpecialites?.length ?? 0) > 0;
}

export function getSelectedProfessionOptions(options: InputFilter[], restriction?: PreferencesRestriction): InputFilter[] {
	if (!restriction) return [];

	// Les professions sont stockées directement par leur identifiant.
	const professionIds = new Set(restriction.autorizedProfessionIds ?? []);
	// Les spécialités sont identifiées par le couple profession/spécialité pour éviter toute ambiguïté.
	const specialiteKeys = new Set((restriction.autorizedSpecialites ?? []).map((specialite) => `profession:${specialite.idProfession}:specialite:${specialite.idSpecialite}`));

	// Une profession cochée autorise implicitement toutes ses spécialités ; sinon, seules les spécialités stockées sont cochées.
	return options.filter((option) =>
		option.filterType === "profession" ? professionIds.has(option.id) : professionIds.has(option.parentProfessionId!) || specialiteKeys.has(option.optionKey)
	);
}

export function normalizeProfessionSelection(options: InputFilter[], currentSelection: InputFilter[], nextSelection: InputFilter[]): ProfessionSpecialiteRestriction {
	// Compare l'état actuel à l'état renvoyé par l'autocomplete à l'aide des clés uniques des options.
	const selectedKeys = new Set(currentSelection.map((option) => option.optionKey));
	const nextSelectedKeys = new Set(nextSelection.map((option) => option.optionKey));
	const professions = options.filter((option) => option.filterType === "profession");

	// Cocher ou décocher une profession applique la même action à toutes ses spécialités.
	for (const profession of professions) {
		const professionWasSelected = selectedKeys.has(profession.optionKey);
		const professionIsSelected = nextSelectedKeys.has(profession.optionKey);
		const relatedSpecialites = options.filter((option) => option.filterType === "specialite" && option.parentProfessionId === profession.id);

		if (professionWasSelected === professionIsSelected) continue;

		if (professionIsSelected) {
			relatedSpecialites.forEach((specialite) => nextSelectedKeys.add(specialite.optionKey));
		} else {
			relatedSpecialites.forEach((specialite) => nextSelectedKeys.delete(specialite.optionKey));
		}
	}

	// Compacte le format sauvegardé : toutes les spécialités sélectionnées sont représentées par leur profession seule.
	for (const profession of professions) {
		const relatedSpecialites = options.filter((option) => option.filterType === "specialite" && option.parentProfessionId === profession.id);
		const allSpecialitesSelected = relatedSpecialites.length > 0 && relatedSpecialites.every((specialite) => nextSelectedKeys.has(specialite.optionKey));

		if (allSpecialitesSelected) {
			nextSelectedKeys.add(profession.optionKey);
			relatedSpecialites.forEach((specialite) => nextSelectedKeys.delete(specialite.optionKey));
		} else if (relatedSpecialites.length > 0) {
			nextSelectedKeys.delete(profession.optionKey);
		}
	}

	// Convertit la sélection normalisée vers le format attendu par l'API.
	const normalizedSelection = options.filter((option) => nextSelectedKeys.has(option.optionKey));
	return {
		// La restriction n'est pas active lorsque toutes les professions sont autorisées.
		limitToAutorizedProfessionsAndSpecialites: !professions.every((profession) => nextSelectedKeys.has(profession.optionKey)),
		autorizedProfessionIds: normalizedSelection.filter((option) => option.filterType === "profession").map((option) => option.id),
		autorizedSpecialites: normalizedSelection
			.filter((option) => option.filterType === "specialite" && option.parentProfessionId !== undefined)
			.map((option) => ({ idProfession: option.parentProfessionId!, idSpecialite: option.id })),
	};
}

export interface SelectedFilterSummary {
	items: SelectedFilterSummaryItem[];
	allItems: SelectedFilterSummaryItem[];
	hiddenCount: number;
	isAllSelected: boolean;
}

export interface SelectedFilterSummaryItem {
	label: string;
	optionKeys: string[];
}

export function getSelectedFilterSummary(options: InputFilter[], selectedOptions: InputFilter[], maximumCharacters = 56): SelectedFilterSummary {
	// Les clés permettent de retrouver les options cochées sans dépendre du libellé affiché.
	const selectedKeys = new Set(selectedOptions.map((option) => option.optionKey));
	const allSelected = options.length > 0 && selectedOptions.length === options.length;
	const allItems: SelectedFilterSummaryItem[] = [];
	const items: SelectedFilterSummaryItem[] = [];
	let hiddenCount = 0;
	let currentLength = 0;
	const professions = options.filter((option) => option.filterType === "profession");

	for (const profession of professions) {
		const specialites = options.filter((option) => option.filterType === "specialite" && option.parentProfessionId === profession.id);
		const isProfessionSelected = selectedKeys.has(profession.optionKey);
		const areAllSpecialitesSelected = specialites.length > 0 && specialites.every((specialite) => selectedKeys.has(specialite.optionKey));
		const professionItems =
			// Une profession complète est résumée par son seul nom ; une sélection partielle détaille ses spécialités.
			isProfessionSelected || areAllSpecialitesSelected
				? [{ label: profession.label, optionKeys: [profession.optionKey, ...specialites.map((specialite) => specialite.optionKey)] }]
				: specialites.filter((specialite) => selectedKeys.has(specialite.optionKey)).map((specialite) => ({ label: specialite.label, optionKeys: [specialite.optionKey] }));

		allItems.push(...professionItems);
		for (const item of professionItems) {
			// Préserve les libellés entiers : un élément ne doit jamais être tronqué dans la chip.
			const nextLength = currentLength + (items.length > 0 ? 2 : 0) + item.label.length;
			if (nextLength <= maximumCharacters || items.length === 0) {
				items.push(item);
				currentLength = nextLength;
			} else hiddenCount++;
		}
	}

	if (allSelected) {
		// Cas particulier demandé dans la spec : toutes les autorisations tiennent dans une seule chip.
		return {
			items: [{ label: "Toutes les professions et spécialités", optionKeys: options.map((option) => option.optionKey) }],
			allItems,
			hiddenCount: 0,
			isAllSelected: true,
		};
	}

	return { items, allItems, hiddenCount, isAllSelected: false };
}
